
export type HandlerFunction<
    T extends TypedEventEmitter<any, any>,
    K extends keyof T[' events']
> = (this: T, detail: T[' events'][K]) => void

export type HandlersRegistry<T extends TypedEventEmitter<any, any>> = {
    [K in keyof T[' events']]?: HandlerFunction<T, K>
}

export type DeserializerFunction<
    T extends TypedEventEmitter<any, any>,
    K extends keyof T[' scheduledEvents'] & keyof T[' events']
> = (this: T, data: T[' scheduledEvents'][K]) => T[' events'][K] | null

export type DeserializerRegistry<T extends TypedEventEmitter<any, any>> = {
    [K in keyof T[' scheduledEvents'] & keyof T[' events']]?: DeserializerFunction<T, K>
}

export interface ScheduledEvent<
    ScheduledEvents extends Record<string, unknown>,
    K extends keyof ScheduledEvents = keyof ScheduledEvents
> {
    event: K & string,
    atTick: number,
    detail: ScheduledEvents[K & string]
}

export class TypedEventEmitter<
    Events extends Record<keyof ScheduledEvents, unknown>,
    ScheduledEvents extends Record<string, unknown>
> extends EventTarget {
    declare ' events': Events
    declare ' scheduledEvents': ScheduledEvents

    static get handlerRegistry(): Record<string, any> {
        if (!Object.hasOwn(this, '_handlerRegistry')) {
            (this as any)._handlerRegistry = {}
        }
        return (this as any)._handlerRegistry
    }

    static get deserializerRegistry(): Record<string, any> {
        if (!Object.hasOwn(this, '_deserializerRegistry')) {
            (this as any)._deserializerRegistry = {}
        }
        return (this as any)._deserializerRegistry
    }

    get handlerRegistry(): Record<string, (data: any) => void> {
        return (this.constructor as any as this).handlerRegistry
    }

    get deserializerRegistry(): Record<string, (data: any) => any> {
        return (this.constructor as any as this).deserializerRegistry
    }

    static registerHandlers<T extends new (...a: any) => TypedEventEmitter<any, any>>(
        this: T,
        handlers: HandlersRegistry<InstanceType<T>>
    ): void {
        const registry = (this as any as InstanceType<T>).handlerRegistry as Record<string, any>
        for (const [event, handler] of Object.entries(handlers)) {
            if (handler) {
                registry[event] = handler
            }
        }
    }

    static registerActionDeserializers<T extends new (...a: any) => TypedEventEmitter<any, any>>(
        this: T,
        deserializersMap: DeserializerRegistry<InstanceType<T>>
    ): void {
        const registry = (this as any as InstanceType<T>).deserializerRegistry as Record<string, any>
        for (const [action, deserializer] of Object.entries(deserializersMap)) {
            if (deserializer) {
                registry[action] = deserializer
            }
        }
    }

    registerHandlers(handlers?: HandlersRegistry<this>): void {
        const handlersToRegister = handlers ?? this.handlerRegistry as HandlersRegistry<this>
        for (const [event, handler] of Object.entries(handlersToRegister)) {
            if (handler) {
                const listener = (e: Event) => handler.call(this, (e as CustomEvent).detail)
                this.addEventListener(event as string, listener)
            }
        }
    }

    static getDeserializerRegistry<T extends new (...a: any) => TypedEventEmitter<any, any>>(
        this: T
    ): DeserializerRegistry<InstanceType<T>> {
        return (this as any as InstanceType<T>).deserializerRegistry
    }

    emit<K extends keyof Events>(type: K, detail: Events[K]) {
        this.dispatchEvent(new CustomEvent(type as string, { detail }))
    }

    on<K extends keyof Events>(
        type: K,
        handler: (detail: Events[K]) => void
    ): () => void {
        const listener = (e: Event) => handler((e as CustomEvent).detail);
        this.addEventListener(type as string, listener);
        return () => this.removeEventListener(type as string, listener);
    }

    addLogging(): void {
        const allEvents = new Set([
            ...Object.keys(this.handlerRegistry),
            ...Object.keys(this.deserializerRegistry)
        ])
        for (const eventName of allEvents) {
            this.on(eventName, (detail) => {
                console.log(`[${String(eventName)}]:`, JSON.stringify(detail));
            });
        }
    }

    get scheduledEvents(): ScheduledEvent<ScheduledEvents>[] {
        throw new Error('scheduledEvents must be implemented by subclass');
    }

    schedule<K extends keyof ScheduledEvents & string>(event: K, atTick: number, detail: ScheduledEvents[K]): void {
        let index = this.scheduledEvents.findIndex(e => e.atTick > atTick);
        if (index === -1) {
            this.scheduledEvents.push({ event, atTick, detail })
        } else {
            this.scheduledEvents.splice(index, 0, { event, atTick, detail })
        }
    }

    executeScheduledEvents(currentTick: number): (
        ScheduledEvent<ScheduledEvents> & {
            processed: Events[keyof Events] | null
        }
    )[] {
        const events: ReturnType<typeof this.executeScheduledEvents> = [];
        while (this.scheduledEvents[0] && this.scheduledEvents[0].atTick <= currentTick) {
            const scheduledEvent = this.scheduledEvents.shift()!;
            const processed = this.processScheduledEvent(scheduledEvent);
            if (processed !== null) {
                events.push({ ...scheduledEvent, processed });
                this.emit(scheduledEvent.event, processed);
            }
        }
        return events;
    }

    processScheduledEvent(event: typeof this.scheduledEvents[number]): Events[keyof Events & string] | null {
        const deserializer = this.deserializerRegistry[event.event];
        if (!deserializer) {
            throw new Error(`No deserializer registered for scheduled event: ${String(event.event)}`);
        }
        return deserializer.call(this, event.detail);
    }
}
